import type { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Result } from 'antd';
import { useIntl } from 'react-intl';
import { SkyButton } from '@skynet/sky-button';

export interface AppErrorBoundaryProps {
  children: ReactNode;
}

function reloadPage() {
  window.location.reload();
}

function AppErrorFallback() {
  const intl = useIntl();

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Result
        status="error"
        title={intl.formatMessage({
          id: 'errorBoundary.title',
          defaultMessage: 'Something went wrong',
        })}
        subTitle={intl.formatMessage({
          id: 'errorBoundary.subTitle',
          defaultMessage: 'An unexpected error occurred. Please reload the page to continue.',
        })}
        extra={
          <SkyButton type="primary" onClick={reloadPage}>
            {intl.formatMessage({ id: 'errorBoundary.reload', defaultMessage: 'Reload page' })}
          </SkyButton>
        }
      />
    </div>
  );
}

/**
 * Top-level boundary rendered above `AntdApp` in {@link ../app.tsx}. Antd's
 * modal/message/notification holders render as siblings of the routed app
 * inside `AntdApp`, outside the route-level `ErrorBoundary` — a render throw
 * there (e.g. a crashing `notification.error` call) previously had nothing to
 * catch it, unmounting the whole tree into a white screen. This boundary
 * catches any such throw below `ThemeWrapper`, including the notification
 * holder inside `AntdApp` and every route (including ones with no
 * route-level boundary, e.g. `/signup`), and shows a fallback instead.
 * `Provider` and `PersistGate` sit outside this boundary, so a throw during
 * redux-persist bootstrap is not covered here.
 */
export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>{children}</Sentry.ErrorBoundary>;
}
